import { FC, PropsWithChildren, Children, useRef, type RefObject } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { styled } from '@mui/material/styles';
import { useContainerWidth } from '../../hooks/useContainerWidth';
import { useColumnsWithWidth } from '../../hooks/useColumnsWithWidth';
import HorizontalScrollDecor from '../HorizontalScrollDecor';
import AccountingTableContext from './AccountingTableContext';
import AccountingRow from './AccountingRow';
import { AccountingColumnHeader, AccountingRowProps } from './types';

export { AccountingRow };
export type { AccountingRowProps };

const AccountingTableRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  containerType: 'inline-size',
});

const Container = styled(Box)({
  width: 'fit-content',
});

const TableTitle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'roundedTop',
})<{ roundedTop?: boolean }>(({ theme, roundedTop }) => ({
  padding: theme.spacing(theme.spacingSizes.sm, theme.spacingSizes.lg),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderBottom: 'none',
  ...(roundedTop && {
    borderTopLeftRadius: Number(theme.shape.borderRadius) * 2,
    borderTopRightRadius: Number(theme.shape.borderRadius) * 2,
  }),
}));

const StyledTable = styled(MuiTable, {
  shouldForwardProp: (prop) =>
    prop !== 'roundedTop' &&
    prop !== 'roundedBottom' &&
    prop !== 'borderHeader',
})<{ roundedTop?: boolean; roundedBottom?: boolean; borderHeader?: boolean }>(
  ({ theme, roundedTop, roundedBottom, borderHeader }) => ({
    borderCollapse: 'separate',
    tableLayout: 'fixed',
    '& .MuiTypography-root': {
      lineHeight: '20px',
      fontSize: '13px',
    },
    ...(roundedTop &&
      !borderHeader && {
        '& > thead > tr > th': {
          borderBottom: 'none',
        },
        '& > tbody > tr:first-of-type > td': {
          borderTop: `1px solid ${theme.palette.grey[300]}`,
        },
        '& > tbody > tr:first-of-type > td:first-of-type': {
          borderTopLeftRadius: Number(theme.shape.borderRadius) * 2,
        },
        '& > tbody > tr:first-of-type > td:last-of-type': {
          borderTopRightRadius: Number(theme.shape.borderRadius) * 2,
        },
      }),
    ...(roundedBottom && {
      '& > tbody > tr:last-of-type > td:first-of-type': {
        borderBottomLeftRadius: Number(theme.shape.borderRadius) * 2,
      },
      '& > tbody > tr:last-of-type > td:last-of-type': {
        borderBottomRightRadius: Number(theme.shape.borderRadius) * 2,
      },
    }),
  }),
);

const RelativeTableBody = styled(TableBody)({
  position: 'relative',
});

const HeaderCell = styled(TableCell, {
  shouldForwardProp: (prop) =>
    prop !== 'paddingY' &&
    prop !== 'alignTop' &&
    prop !== 'alignBottom' &&
    prop !== 'bordered' &&
    prop !== 'hidden' &&
    prop !== 'fontSize' &&
    prop !== 'lineHeight' &&
    prop !== 'indentation',
})<{
  paddingY?: number | string;
  alignTop?: boolean;
  alignBottom?: boolean;
  bordered?: boolean;
  hidden?: boolean;
  fontSize?: number | string;
  lineHeight?: number | string;
  indentation?: number;
}>(
  ({
    theme,
    paddingY,
    alignTop,
    alignBottom,
    bordered,
    hidden,
    fontSize,
    lineHeight,
    indentation,
  }) => ({
    color: theme.palette.grey[500],
    fontSize: hidden ? 0 : fontSize,
    lineHeight: hidden ? 0 : lineHeight,
    whiteSpace: 'nowrap',
    paddingTop: hidden ? 0 : paddingY,
    paddingBottom: hidden ? 0 : paddingY,
    ...(hidden && {
      border: 'none',
      height: 0,
      overflow: 'hidden',
    }),
    ...(alignTop && {
      verticalAlign: 'top',
    }),
    ...(alignBottom && {
      verticalAlign: 'bottom',
    }),
    ...(bordered && {
      borderTop: `1px solid ${theme.palette.grey[300]}`,
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      '&:last-of-type': {
        borderRight: `1px solid ${theme.palette.grey[300]}`,
      },
    }),
    '&:first-of-type': {
      ...(bordered && {
        borderLeft: `1px solid ${theme.palette.grey[300]}`,
      }),
      ...(indentation !== undefined && {
        paddingLeft: indentation,
      }),
    },
  }),
);

const HeaderContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hidden',
})<{ hidden?: boolean }>(({ hidden }) => ({
  ...(hidden && {
    height: 0,
    overflow: 'hidden',
    visibility: 'hidden',
  }),
}));

const ScrollSurface = styled(Box)({
  width: '100%',
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
});

const EmptyMessageViewportCenter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xl, theme.spacingSizes.sm),
  position: 'sticky',
  left: 0,
  boxSizing: 'border-box',
  width: '100cqw',
  maxWidth: '100cqw',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

type AccountingTableProps = PropsWithChildren<{
  headerFontSize?: number | string;
  headerLineHeight?: number | string;
  headerPaddingY?: number | string;
  headerIndentation?: number;
  columnHeaders: Array<AccountingColumnHeader>;
  rounded?: boolean;
  headerVisible?: boolean;
  borderHeader?: boolean;
  title?: string;
  emptyMessage?: string;
  showSwipeHint?: boolean;
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
}>;

const AccountingTable: FC<AccountingTableProps> = ({
  children,
  headerFontSize,
  headerLineHeight,
  headerPaddingY,
  headerIndentation,
  columnHeaders,
  rounded = false,
  headerVisible = true,
  borderHeader = false,
  title,
  emptyMessage,
  showSwipeHint = false,
  scrollContainerRef,
}) => {
  const isExternalScroll = scrollContainerRef != null;
  const { width, ref } = useContainerWidth(
    isExternalScroll ? scrollContainerRef : undefined,
  );
  const tableContentRef = useRef<HTMLDivElement | null>(null);
  const resolvedColumns = useColumnsWithWidth(columnHeaders, width);
  const columnCount = resolvedColumns.length;
  const hasNoChildren = Children.toArray(children).length === 0;
  const showBorderHeader = headerVisible ? borderHeader : false;
  const showHeaderCellBorder = headerVisible ? !!title || borderHeader : false;

  if (isExternalScroll) {
    return (
      <AccountingTableContext.Provider value={{ columnCount }}>
        <Container>
          {title ? (
            <TableTitle roundedTop={rounded}>
              <Typography variant="h6">{title}</Typography>
            </TableTitle>
          ) : null}
          <StyledTable
            roundedTop={title ? false : rounded}
            roundedBottom={rounded}
            borderHeader={showBorderHeader}
          >
            <colgroup>
              {resolvedColumns.map(({ key, width }) => (
                <col key={key} style={{ width }} />
              ))}
            </colgroup>
            <TableHead aria-hidden={!headerVisible}>
              <TableRow>
                {resolvedColumns.map(
                  ({ key, label, width, paddingY, alignTop, alignBottom }) => (
                    <HeaderCell
                      key={key}
                      width={width}
                      hidden={!headerVisible}
                      fontSize={headerFontSize}
                      lineHeight={headerLineHeight}
                      paddingY={paddingY ?? headerPaddingY}
                      alignTop={alignTop}
                      alignBottom={alignBottom}
                      bordered={showHeaderCellBorder}
                      indentation={headerIndentation}
                    >
                      <HeaderContent hidden={!headerVisible}>
                        {label}
                      </HeaderContent>
                    </HeaderCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <RelativeTableBody>
              {hasNoChildren && emptyMessage ? (
                <TableRow>
                  <TableCell colSpan={columnCount} padding="none">
                    <EmptyMessageViewportCenter>
                      <Typography
                        variant="body2"
                        textAlign="center"
                        color="grey.500"
                      >
                        {emptyMessage}
                      </Typography>
                    </EmptyMessageViewportCenter>
                  </TableCell>
                </TableRow>
              ) : (
                children
              )}
            </RelativeTableBody>
          </StyledTable>
        </Container>
      </AccountingTableContext.Provider>
    );
  }

  return (
    <AccountingTableContext.Provider value={{ columnCount }}>
      <AccountingTableRoot>
        <ScrollSurface ref={ref}>
          <Container ref={tableContentRef}>
            {title ? (
              <TableTitle roundedTop={rounded}>
                <Typography variant="h6">{title}</Typography>
              </TableTitle>
            ) : null}
            <StyledTable
              roundedTop={title ? false : rounded}
              roundedBottom={rounded}
              borderHeader={showBorderHeader}
            >
              <colgroup>
                {resolvedColumns.map(({ key, width }) => (
                  <col key={key} style={{ width }} />
                ))}
              </colgroup>
              <TableHead aria-hidden={!headerVisible}>
                <TableRow>
                  {resolvedColumns.map(
                    ({
                      key,
                      label,
                      width,
                      paddingY,
                      alignTop,
                      alignBottom,
                    }) => (
                      <HeaderCell
                        key={key}
                        width={width}
                        hidden={!headerVisible}
                        fontSize={headerFontSize}
                        lineHeight={headerLineHeight}
                        paddingY={paddingY ?? headerPaddingY}
                        alignTop={alignTop}
                        alignBottom={alignBottom}
                        bordered={showHeaderCellBorder}
                        indentation={headerIndentation}
                      >
                        <HeaderContent hidden={!headerVisible}>
                          {label}
                        </HeaderContent>
                      </HeaderCell>
                    ),
                  )}
                </TableRow>
              </TableHead>
              <RelativeTableBody>
                {hasNoChildren && emptyMessage ? (
                  <TableRow>
                    <TableCell colSpan={columnCount} padding="none">
                      <EmptyMessageViewportCenter>
                        <Typography
                          variant="body2"
                          textAlign="center"
                          color="grey.500"
                        >
                          {emptyMessage}
                        </Typography>
                      </EmptyMessageViewportCenter>
                    </TableCell>
                  </TableRow>
                ) : (
                  children
                )}
              </RelativeTableBody>
            </StyledTable>
          </Container>
        </ScrollSurface>
        <HorizontalScrollDecor
          scrollContainerRef={ref}
          contentRef={tableContentRef}
          showSwipeHint={showSwipeHint}
        />
      </AccountingTableRoot>
    </AccountingTableContext.Provider>
  );
};

export default AccountingTable;
